import { t } from 'i18next';

/**
 * Browser-native fetch failure messages (request failed before response).
 * Chrome/Edge: "Failed to fetch"
 * Firefox: "NetworkError when attempting to fetch resource"
 * Safari: "Load failed"
 */
const NETWORK_FETCH_ERROR_STRING_PATTERNS = [
  'failed to fetch',
  'networkerror when attempting to fetch resource',
  'load failed',
];

/**
 * Detects whether an error is a network/fetch failure (request failed before
 * receiving a response). These errors are opaque and benefit from contextual messaging.
 */
export function isNetworkFetchError(error: unknown): boolean {
  if (error == null) return false;
  const message =
    typeof (error as Error).message === 'string'
      ? (error as Error).message
      : '';
  const name =
    typeof (error as Error).name === 'string' ? (error as Error).name : '';
  const lowerCasedMessage = message.toLowerCase();
  const isKnownMessage = NETWORK_FETCH_ERROR_STRING_PATTERNS.some((pattern) =>
    lowerCasedMessage.includes(pattern),
  );
  // Fallback: TypeError with "fetch" in message (e.g. other runtimes or messages we didn't add to the pattern list)
  const isTypeErrorFetch =
    name === 'TypeError' && lowerCasedMessage.includes('fetch');
  return isKnownMessage || isTypeErrorFetch;
}

/**
 * Returns a user-friendly, localized message for network/fetch errors.
 * Uses navigator.onLine when available to suggest checking connection.
 */
export function getNetworkErrorMessage(): string {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return t(
      'Unable to connect. Please check your internet connection and try again.',
    );
  }
  return t(
    'The request could not be completed. This may be due to a connection issue, server unavailability, or a temporary network problem. Please try again.',
  );
}
